import { StarLanguage } from 'shared/constants/AppEnum'
import { OfferingPoolResponseType } from 'store/reducers/temple/offeringPoolSlice'
import { IDeityType, IDonationBaseType, IDonationCategory, IOfferingBaseType, IOfferingCategory, IOfferingTag, IOfferingTime } from 'types/store/temple'

export type TempleIdType = { templeId?: string | number }

export type AddressResponseType = {
    address_line_1: string,
    address_line_2: string | null,
    city: string,
    district: string,
    state: string,
    pincode: string,
}

export type LatLngType = {
    latitude: number,
    longitude: number,
}
export type LocationResponseType = {
    latlng: LatLngType,
    formattedAddress?: string | null
}

export type TempleAdminsType = {
    user: PublicUserResponseType, role: string
}

export enum UserRoleEnum {
    staff = 'staff',
    admin = 'administrator'
}

export type TInviteForm = {
    email: string;
    role: UserRoleEnum
}


export type TempleRequestUserRoleTypes = 'administrator' | 'manager'
export type ITempleResponse = {
    name: string,
    counterName: string,
    displayImg: ImageResponse | null,
    coverImg: ImageResponse | null,
    logoImg: ImageResponse | null,
    description?: string | null
    email?: string,
    contact_no_1?: string | null,
    contact_no_2?: string | null,
    address?: AddressResponseType,
    starLang?: StarLanguage
    phone?: string
    location?: LocationResponseType,
    isActive?: boolean,
    admins?: TempleAdminsType[]
    tds?: number
    bankAccounts?: string[]
    createdAt?: string
    updatedAt?: string
    requestUserRole?: TempleRequestUserRoleTypes
    openingTime?: string | null
    closingTime?: string | null
    offlineConsolidationTime?: string | null
    whatsappNo?: string | null,
    commissionPercent?: number | null,
    commissionMin?: number | null,
    commissionMax?: number | null,
    defaultCourierCharge?: number | null,
    /**
     * Default payment gateway for the temple. Allowed values:
     * - 'RP' (Razorpay)
     * - 'EB' (Easebuzz)
     * - 'CL' (Clover)
     */
    defaultGateway?: 'RP' | 'EB' | 'CL',
    /**
     * Calendar configuration used by various modules. Allowed values:
     * - 'koz' (Kozhikode)
     * - 'tvm' (Thiruvananthapuram)
     */
    calendar?: 'koz' | 'tvm',
    enableGstOnStore: boolean,
    dakshinaInConsolidation: boolean
    // Changed on 05/03
    enableDakshina: boolean,
    enableTbaBooking: boolean,
    tbaUpfrontBookingPercentage?: number | null,
    enableOffering: boolean,
    enableFinancial: boolean,
    allowZeroAmountTransactions?: boolean,
    enableInventory: boolean,
    enableDonation: boolean,
    enableStore: boolean,
    enableMarriage: boolean,
    enableHall: boolean,
    enableRoom: boolean,
    enableElephant: boolean,
    resetRefOnYearChange?: boolean,
    refNoPadding?: number,
    orderRefNoTpl?: string,
    offeringReceiptRefNoTpl?: string,
    donationReceiptRefNoTpl?: string,
    voucherRefNoTpl?: string,
    invoiceRefNoTpl?: string,
    slug: string
} & IdAndUrlType

export type BankAccountType = IdType & Partial<UrlType> & {
    isActive: boolean,
    accName: string,
    accNumber: string,
    bankName: string,
    branchName: string,
    ifsc: string,
    cancelledCheque?: File,
    createdAt?: string,
    updatedAt?: string,
    merchantId?: string,
    temple?: string
}

export type RazorpayBankAccountType = IdAndUrlType & {
    merchantId: string,
    bankAccount: string,
    temple?: string
}

export type EasebuzzSubmerchantType = IdAndUrlType & {
    temple?: string | number,
    bankAccount: string,
    subMerchantName: string,
    subMerchantEmail: string,
    subMerchantPhone: string,
    submerchantId?: string | null,
    status?: boolean,
    kycBankProof?: string | null,
    kycIdProof?: string | null,
    kycExtraDoc?: string | null,
    dcDeductionPercentageLtOneThousand?: number | null,
    dcDeductionPercentageLtTwoThousand?: number | null,
    dcDeductionPercentageGtTwoThousand?: number | null,
    nbDeduction?: number | null,
    isNbRsPercentage?: boolean,
    upiDeductionPercentageLtLimit?: number | null,
    upiDeductionPercentageGtLimit?: number | null,
    upiLimit?: number | null,
    isUpiRsPercentage?: boolean,
    cashcardDeductionPercentage?: number | null,
    debitatmDeductionPercentage?: number | null,
    otherDeductionPercentage?: number | null,
    subMerchantGstin?: string | null,
    subMerchantBusinessName?: string | null,
    subMerchantBusinessAddress?: string | null,
    subMerchantState?: string | null,
    subMerchantBusinessNature?: string | null,
    responseData?: any,
    createdAt?: string,
    updatedAt?: string
}

export type IOfferingCategoryPartialResponse = IOfferingCategory & IdAndUrlType

export type IOfferingCategoryResponse =
    TempleIdType &
    IOfferingCategoryPartialResponse &
    { offerings: number[] | string[] }

export type ITagResponse = IdAndUrlType & {
    name: string,
    code: string,
    description: string,
    isActive: boolean,
    sortOrder: number,
    category: string
}

export type IOfferingTagResponse =
    TempleIdType &
    IdAndUrlType &
    IOfferingTag & {
        tagName?: string
    }

// export type IOfferingCategoryErr = 
//     IOfferingCategoryResponse |
//     IOfferingCategoryResponse[] |
//     ApiErrors<IOfferingCategoryResponse>
export type IDonationCategoryPartialResponse = IDonationCategory & IdAndUrlType

export type IDonationCategoryResponse =
    TempleIdType & IDonationCategoryPartialResponse &
    { donations: number[] | string[] }


export type ParamWithUrlType = {
    itemId: number | string
} | UrlType

export type ParamsOrUrlType<T> = UrlType | T


// export type PartialTempleKeys = "id" | "url" | "name" | "displayImg" | "coverImg"

// export type TemplePartialType = Pick<ITempleResponse, PartialTempleKeys>
// export type TempleListType = TemplePartialType[]
// export type TempleType = ITempleResponse | TemplePartialType

export type IOfferingTimeResponse = Omit<IOfferingTime, 'poolConnections'> & IdAndUrlType & {
    poolConnections: {
        id: string | number,
        pool: OfferingPoolResponseType
    }[]
}

export type IOfferingResponse =
    TempleIdType &
    IOfferingBaseType &
    IdAndUrlType &
    {
        category: string
        offeringTimes?: IOfferingTimeResponse[],
        poolConnections: {
            id: string | number,
            pool: OfferingPoolResponseType
        }[]
    }

export type IDonationResponse =
    TempleIdType &
    IDonationBaseType &
    IdAndUrlType &
    {
        category: string
    }

export type IDeityResponse =
    TempleIdType &
    IDeityType &
    IdAndUrlType &
    {
        offerings: string[]
    }



export type UsersResponseType = {
    id: string
    temple: string
    user: PublicUserResponseType
    permissions: string[]
    transactionViewAllowedUsers?: number[]
    role: string
    createdAt: string
    updatedAt: string
}

export type StaffProfileRoleType = 'staff' | 'priest' | 'other'

export type PriestType = 'vaishnava' | 'shiva' | 'other'

export type StaffProfilePayloadType = {
    templeAdmin: string | number
    roleType: StaffProfileRoleType
    priestType?: PriestType | null
    languages: string[]
    excludedOfferings: (string | number)[]
    notes: string
    isActive: boolean
}

export type StaffProfileResponseType = StaffProfilePayloadType & IdType & TempleIdType & {
    user: number
    firstName: string
    lastName: string
    fullName: string
    phone: string
    email: string
    role: string
    createdAt: string
    updatedAt: string
}

export type StaffTimeOffModeType = 'time_off' | 'recurrence'

export type StaffLeaveType = 'leave' | 'permission'

export type StaffSessionType = 'fn' | 'an' | 'full_day' | 'time_based'

export type StaffRecurrenceType = 'none' | 'daily' | 'weekly' | 'monthly'

export type StaffTimeOffConflictType = {
    occurrence: number
    date: string
    startTime: string
    endTime: string
    serviceName: string
    venue?: number | null
    venueName?: string | null
    availablePriests: number[]
}

export type StaffTimeOffPayloadType = {
    staffProfile: string | number
    mode: StaffTimeOffModeType
    leaveType: StaffLeaveType
    sessionType: StaffSessionType
    startDate: string
    endDate: string
    startTime?: string | null
    endTime?: string | null
    recurrence: StaffRecurrenceType
    recurrenceInterval: number
    reason: string
    isActive?: boolean
}

export type StaffTimeOffResponseType = StaffTimeOffPayloadType & IdType & TempleIdType & {
    staffName: string
    conflicts: StaffTimeOffConflictType[]
    isActive: boolean
    createdAt: string
    updatedAt: string
}

export type StaffCalendarSourceType = 'assignment' | 'time_off'

export type StaffCalendarStatusType = StaffCalendarSourceType | 'scheduled' | 'cancelled' | 'recurrence'

export type StaffCalendarFilterType = {
    dateFrom: string
    dateTo: string
    staff?: string | number
    role?: string
    status?: StaffCalendarStatusType | ''
    templeId?: string | number
}

export type StaffCalendarRowType = {
    sourceType: StaffCalendarSourceType
    sourceId: number
    date: string
    startTime: string
    endTime: string
    status: string
    staffProfile: number
    staffName: string
    label: string
    venue?: number | null
    venueName?: string | null
}

export type StaffAssignmentStatusType = 'scheduled' | 'cancelled'

export type StaffPujaAssignmentPayloadType = {
    staffProfile: string | number
    offering: string | number
    venue?: string | number | null
    startDate: string
    endDate: string
    startTime: string
    endTime: string
    recurrence: StaffRecurrenceType
    recurrenceInterval: number
    displayInHomePage: boolean
    displayInTempleCalendar: boolean
    annualPuja: boolean
    occurrenceCount?: number | null
    notes: string
    status: StaffAssignmentStatusType
}

export type StaffPujaAssignmentResponseType = StaffPujaAssignmentPayloadType & IdType & TempleIdType & {
    staffName: string
    offeringName: string
    venueName?: string | null
    occurrenceTotal: number
    createdAt: string
    updatedAt: string
}

export type StaffPujaAssignmentOccurrenceResponseType = {
    id: number
    assignment: number
    assignmentName: string
    staffProfile: number
    staffName: string
    venue?: number | null
    venueName?: string | null
    date: string
    startTime: string
    endTime: string
    status: StaffAssignmentStatusType
    notes: string
    createdAt: string
    updatedAt: string
}

export type TUserPermissions = {
    [category: string]: string[][]
}

export type TUpdatePermissionsPayload = {
    userId: string,
    data: Partial<UsersResponseType>
}
